import React, { useEffect, useRef, useState } from 'react';
import projectIllustration from '../../assets/images/landing/project-development.svg';
import { STEPS } from './landingContent';

const STEP_MS = 3200;
const PAUSE_AFTER_CLICK_MS = 5000;

const HowWeWork = () => {
 const sectionRef = useRef(null);
 const pauseUntil = useRef(0);
 const [active, setActive] = useState(0);
 const [inView, setInView] = useState(false);
 const [reduceMotion, setReduceMotion] = useState(false);

 const count = STEPS.length;

 useEffect(() => {
 const media = window.matchMedia('(prefers-reduced-motion: reduce)');
 const sync = () => setReduceMotion(media.matches);
 sync();
 if (media.addEventListener) {
 media.addEventListener('change', sync);
 return () => media.removeEventListener('change', sync);
 }
 media.addListener(sync);
 return () => media.removeListener(sync);
 }, []);

 useEffect(() => {
 const node = sectionRef.current;
 if (!node || !('IntersectionObserver' in window)) {
 setInView(true);
 return undefined;
 }
 const observer = new IntersectionObserver(
 ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio > 0.25),
 { threshold: [0, 0.25, 0.5] }
 );
 observer.observe(node);
 return () => observer.disconnect();
 }, []);

 useEffect(() => {
 if (reduceMotion || !inView) {
 return undefined;
 }
 const tick = () => {
 if (Date.now() < pauseUntil.current) {
 return;
 }
 setActive((prev) => (prev + 1) % count);
 };
 const id = window.setInterval(tick, STEP_MS);
 return () => window.clearInterval(id);
 }, [count, inView, reduceMotion]);

 const selectStep = (index) => {
 setActive(index);
 pauseUntil.current = Date.now() + PAUSE_AFTER_CLICK_MS;
 };

 const onStepKeyDown = (event, index) => {
 if (event.key === 'Enter' || event.key === ' ') {
 event.preventDefault();
 selectStep(index);
 }
 };

 const reached = (active + 1) / count;

 return (
 <section
 className="lp-section lp-section-paper lp-how"
 id="how-we-work"
 ref={sectionRef}
 aria-labelledby="how-title"
 >
 <span className="lp-capsule-fill lp-how-capsule" aria-hidden="true" />
 <span className="lp-capsule-line lp-how-capsule-line" aria-hidden="true" />
 <div className="lp-wrap">
 <div className="lp-head lp-head-art lp-reveal">
 <div>
 <p className="lp-eyebrow">04 - How we work</p>
 <h2 id="how-title">
 From first call <span className="lp-accent-sans">to governed delivery.</span>
 </h2>
 <div className="lp-how-meter" aria-hidden="true">
 <span className="lp-how-meter-label">
 Step {String(active + 1).padStart(2, '0')} of {String(count).padStart(2, '0')}
 </span>
 <span className="lp-how-meter-track">
 {STEPS.map((step, index) => (
 <i
 key={step.title}
 className={
 index < active ? 'is-done' : index === active ? 'is-active' : undefined
 }
 />
 ))}
 </span>
 </div>
 </div>
 <img
 className="lp-art lp-art-project"
 src={projectIllustration}
 alt=""
 width="260"
 height="202"
 />
 </div>
 <ol className="lp-steps">
 {STEPS.map((step, index) => {
 const isActive = index === active;
 const isDone = index <= active;
 return (
 <li
 key={step.title}
 className="lp-reveal lp-step"
 data-done={isDone ? '' : undefined}
 data-active={isActive ? '' : undefined}
 aria-current={isActive ? 'step' : undefined}
 >
 <div
 className="lp-step-hit"
 role="button"
 tabIndex={0}
 aria-pressed={isActive}
 aria-label={`Step ${index + 1}: ${step.title}`}
 onClick={() => selectStep(index)}
 onKeyDown={(event) => onStepKeyDown(event, index)}
 >
 <span className="lp-step-num" aria-hidden="true">
 0{index + 1}
 </span>
 <span className="lp-step-track" aria-hidden="true">
 <span className="lp-step-dot" />
 <span
 className="lp-step-fill"
 style={{
 transform: `scaleX(${Math.max(0, Math.min(1, reached * count - index))})`
 }}
 />
 </span>
 <h3>{step.title}</h3>
 <p>{step.body}</p>
 <ul>
 {step.outcomes.map((item) => (
 <li key={item}>
 <span aria-hidden="true">✓</span> {item}
 </li>
 ))}
 </ul>
 <span className="lp-step-indicator" aria-hidden="true" />
 </div>
 </li>
 );
 })}
 </ol>
 </div>
 </section>
 );
};

export default HowWeWork;
